<template>
	<FlagshipFundHero :fund-aum-label="fundAumLabel" :fund-aum-date="fundAumDate">
		<template #lead-capture>
			<div
				:class="[
					'display-flex flex-direction-column gap-100 mt-200 width-100',
					{ 'flex-direction-column-reverse': thirdPartyAuthStore.isSignupEnabled }
				]"
			>
				<MarketingHeroCta
					:center-button="true"
					:is-dark-mode="true"
					:form-config="leadCaptureFormConfig"
					:offering-id="flagshipFundOfferingId"
				/>
				<p class="body-sm text-color-gray-20 mt-0"
					>Create your free account and start investing in less than 5 minutes.</p
				>
			</div>
		</template>
	</FlagshipFundHero>
	<BaseLayout
		:transparent-header="true"
		:footer-full="true"
		:hide-header="true"
		:include-padding="false"
		background-color-class="bg-color-content-primary"
	>
		<FlagshipFundWhyPrivateRealEstateSection v-if="!showSimplifiedPage" />
		<FlagshipFundStrategiesSection v-if="!showSimplifiedPage" />
		<ExploreAllProjectsSection v-if="!showSimplifiedPage" :offering-id="flagshipFundOfferingId" />

		<section class="marketing-section bg-color-white py-400-md">
			<div class="container">
				<h2 class="heading-5 mb-50-smo mb-150-md">Literature</h2>
				<LiteratureSection
					v-if="showLiterature"
					:literature-list="literatureItems"
					:disclaimer="literatureDisclaimer"
					disclaimer-classes="body-lg-md mt-150"
				/>
			</div>
		</section>

		<FlagshipFundEmailCaptureSection v-if="!showSimplifiedPage">
			<template #lead-capture>
				<div
					:class="[
						'display-flex flex-direction-column',
						{ 'flex-direction-column-reverse gap-100': thirdPartyAuthStore.isSignupEnabled }
					]"
				>
					<MarketingHeroCta
						class="width-100 px-50"
						:center-button="true"
						:form-config="leadCaptureFormConfig"
						:offering-id="flagshipFundOfferingId"
						component-id="flagship-fund"
					/>
					<p class="body-sm text-color-white text-align-center"
						>Start investing in less than 5 minutes and with as little as $10.</p
					>
				</div>
			</template>
		</FlagshipFundEmailCaptureSection>
		<template #footer>
			<FooterFull>
				<template #additionalDisclaimer>
					<p>Fundrise Flagship Fund is the marketing name for Fundrise Real Estate Interval Fund, LLC.</p>
					<p
						>The originating advertising material was sponsored and paid for by the Fundrise Real Estate
						Interval Fund, LLC. The originating advertising material is a paid endorsement. Nothing in the
						advertising material should be construed as tax advice, an offer, recommendation, or
						solicitation to buy or sell any security. All investing includes risk, including the possible
						loss of money you invest, and past performance does not guarantee future performance. Carefully
						consider the investment objectives, risks, charges and expenses of the Fundrise Real Estate
						Interval Fund, LLC before investing.</p
					>
					<br />
				</template>
			</FooterFull>
		</template>
	</BaseLayout>
</template>

<script setup lang="ts">
import { useHead } from '@unhead/vue';
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import FooterFull from '@components/footer/footer-full.vue';
import ExploreAllProjectsSection from '@components/marketing-site/explore-all-projects-section.vue';
import FlagshipFundEmailCaptureSection from '@components/marketing-site/flagship-fund/flagship-fund-email-capture-section.vue';
import FlagshipFundHero from '@components/marketing-site/flagship-fund/flagship-fund-hero.vue';
import FlagshipFundStrategiesSection from '@components/marketing-site/flagship-fund/flagship-fund-strategies-section.vue';
import FlagshipFundWhyPrivateRealEstateSection from '@components/marketing-site/flagship-fund/flagship-fund-why-private-real-estate-section.vue';
import MarketingHeroCta from '@components/marketing-site/marketing-hero-cta.vue';
import LiteratureSection from '@components/offering-details/literature-section.vue';
import { marketingFunnelFundRealEstateInterval } from '@constants/plans-info';
import { flagshipFundOfferingId } from '@constants/reit';
import { getFund } from '@sanity/proxy/funds';
import { useThirdPartyAuthStore } from '@stores/third-party-auth';
import { flattenQueryParam } from '@utils/request';
import { FundLiterature } from 'types/funds';
import { LeadCaptureFormConfig } from 'types/layout/lead';
import { SanityRichTextBlock } from 'types/sanity';

const route = useRoute();
const thirdPartyAuthStore = useThirdPartyAuthStore();

const metaTitle = 'The Fundrise Flagship Fund';
const description =
	'Our billion-dollar real estate fund is designed to offer steady, long-term growth—a timeless strategy to help build wealth and portfolio balance.';

const literatureItems = ref<Array<FundLiterature>>([]);
const literatureDisclaimer = ref<Array<SanityRichTextBlock>>([]);
const fundAumLabel = ref('');
const fundAumDate = ref('');
const loading = ref(true);
const showSimplifiedPage = ref(true);

const leadCaptureFormConfig: LeadCaptureFormConfig = {
	buttonText: 'Get started',
	centerButton: true,
	presetPlanId: marketingFunnelFundRealEstateInterval,
	signupFlowType: 'FUND_MARKETING',
	thirdPartyAuthTheme: 'SECONDARY'
};

const showLiterature = computed((): boolean => literatureItems.value?.length > 0);

async function setup(): Promise<void> {
	useHead({
		title: metaTitle,
		meta: [
			{ property: 'og:title', content: metaTitle },
			{ name: 'description', content: description },
			{ property: 'og:description', content: description }
		]
	});

	try {
		const utmMediumsForLongPage = ['podcast', 'podcasts', 'directmail', 'mediapartner'];
		const utmMedium = flattenQueryParam(route.query.utm_medium);
		const isUtmMediumForLongPage = utmMedium && utmMediumsForLongPage.includes(utmMedium.toLowerCase());

		if (isUtmMediumForLongPage) {
			showSimplifiedPage.value = false;
		}

		const fundDetails = await getFund(flagshipFundOfferingId);

		literatureItems.value = fundDetails?.literature ?? [];
		literatureDisclaimer.value = fundDetails?.literatureDisclaimer ?? [];
		fundAumLabel.value = fundDetails?.fundAum ?? '';
		fundAumDate.value = fundDetails?.fundAumUpdateDate ?? '';
	} catch {
	} finally {
		loading.value = false;
	}
}

setup();
</script>
